// Packages
import React, { useState, useEffect, useMemo } from "react";
import useTableInstance from "hooks/useTableInstance";
import { createColumnHelper } from "@tanstack/react-table";
import { Box, IconButton, Typography } from "@mui/material";
import EllipsisTooltipCell from "utils/ellipsisTooltipCell";
import { useNavigate, useOutletContext } from "react-router-dom";
import { useTheme } from "@mui/material/styles";

// Utils
import localStorageUtil from 'utils/localStorageUtil';

// hooks

// Components
import TableField from "components/Table";
import CollapseCard from "components/Collapse";

// Img
import { ReactComponent as PencilIcon } from "assets/images/icons/icn_table_cell_Pencil_Writing.svg";


const DEFAULT_DISPLAY_COUNT = 2;

const WirelessPanel = ({ auth, infoData, wirelessNetworkData, capabilityData }) => {
  const theme = useTheme();
  const { scopeData } = useOutletContext();
  const navigate = useNavigate();
  const columnHelper = createColumnHelper();

  const [showAll, setShowAll] = useState(false);

  const tableData = useMemo(() => {
    const radios = Array.isArray(capabilityData?.radios)
      ? capabilityData.radios.map(String)
      : [];

    const filtered = (
      Array.isArray(wirelessNetworkData) ? wirelessNetworkData : []
    )
      .map((item) => {
        const bands = Array.isArray(item?.band) ? item.band.map(String) : [];
        const supportedBands = bands.filter((b) => radios.includes(b));
        return { ...item, band: supportedBands };
      })
      .filter((item) => Array.isArray(item.band) && item.band.length > 0);

    if (showAll) return filtered;

    return filtered.length > 0 ? filtered.slice(0, DEFAULT_DISPLAY_COUNT) : [];
  }, [wirelessNetworkData, capabilityData?.radios, showAll]);

  const showMore =
    Array.isArray(wirelessNetworkData) &&
    wirelessNetworkData.length > DEFAULT_DISPLAY_COUNT;

  const handleToggleShow = () => {
    setShowAll((prev) => !prev);
  };

  const columnsData = [
    columnHelper.accessor("ssid", {
      header: "Name",
      cell: (info) => {
        const value = info.getValue();
        return <EllipsisTooltipCell text={value} />;
      },
    }),
    columnHelper.accessor("band", {
      header: "Band",
      cell: (info) => {
        const value = info.getValue();
        if (!value || (Array.isArray(value) && value.length === 0)) return "-";
        const formatBand = (band) => {
          if (band === "2" || band === 2) return "2.4GHz";
          if (band === "5" || band === 5) return "5GHz";
          if (band === "6" || band === 6) return "6GHz";
          return `${band}GHz`;
        };

        if (Array.isArray(value)) {
          return (
            <div style={{ whiteSpace: "pre-line" }}>
              {value.map((b, i) => (
                <div key={i}>{formatBand(b)}</div>
              ))}
            </div>
          );
        }
        return formatBand(value);
      },
    }),

    columnHelper.accessor("client", {
      header: "Client",
      cell: (info) => {
        const value = info.getValue();
        if (!Number.isFinite(value)) return "-";
        return value;
      },
    }),
    columnHelper.display({
      id: "action",
      header: "Action",
      cell: () => (
        <Box sx={{ display: "flex", gap: 1 }}>
          <IconButton
            onClick={() => {
              if (auth.isMspDeviceOwner) {
                localStorageUtil.updateItemField("selectBreadcrumbs", "orgs", {
                  id: infoData.orgId,
                  name: infoData.orgName,
                });
              }

              if (auth.scopeLevel === "org") {
                const selectedSite = scopeData.site.find(site => site.id === infoData.siteId);
                localStorageUtil.updateItemField(
                  "selectBreadcrumbs",
                  "site",
                  selectedSite || {
                    id: infoData.siteId,
                    name: infoData.siteName,
                  },
                );
              }

              navigate("/site/settings", {
                state: {
                  navigateTab: "wireless-ssid"
                }
              });
            }}>
            <PencilIcon />
          </IconButton>
        </Box>
      ),
    }),
  ];

  const table = useTableInstance({
    tableName: "wireless",
    columns: columnsData,
    data: tableData,
    enableSorting: true,
    enablePagination: false,
    enableGlobalFilter: true,
    enableRowSelection: true,
  });

  return (
    <CollapseCard title="Wireless SSID">
      <Box
        sx={{
          maxWidth: 560,
          width: "100%",
          mx: "auto",
        }}
      >
        <TableField {...table} />

        {showMore && (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
            }}
          >
            <Typography
              onClick={handleToggleShow}
              variant="text"
              sx={{
                minWidth: 0,
                padding: 0,
                color: theme.palette.primary.greySecound,
                cursor: "pointer",
              }}
            >
              {showAll ? "" : "More..."}
            </Typography>
          </Box>
        )}
      </Box>
    </CollapseCard>
  );
};

export default WirelessPanel;
